/**
 * Hook centralizado para datos del usuario actual con actualización en tiempo real.
 * Combina el perfil completo, imágenes de avatar y suscripciones a cambios en tiempo real.
 * 
 * @returns {Object} - Objeto con datos, loading, error y funciones de utilidad
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

// Variable global para almacenar el canal del perfil por usuario
const globalProfileChannels = new Map();

/**
 * Hook personalizado para obtener y mantener actualizados los datos del usuario actual
 * Incluye perfil completo, avatar y suscripción a cambios en tiempo real
 */
export const useCurrentUserProfile = () => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id;

  // Ref para identificar este componente específico
  const componentIdRef = useRef(`profile-comp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`);

  /**
   * Función para obtener el perfil del usuario actual
   */
  const fetchUserProfile = useCallback(async () => {
    if (!userId) {
      throw new Error('Usuario no autenticado');
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;

      // Obtener la URL del avatar desde user_images filtrando por album 'fotosDePerfil'
      // Ordenar por created_at DESC para obtener la imagen más reciente
      const { data: imageData, error: imageError } = await supabase
        .from('user_images')
        .select(`
          url,
          album_images!inner (
            albums!inner (
              name
            )
          )
        `)
        .eq('user_ref_id', userId)
        .eq('is_deleted', false)
        .eq('album_images.albums.name', 'fotosDePerfil')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (imageError && imageError.code !== 'PGRST116') {
        console.error('[useCurrentUserProfile] Error obteniendo imagen:', imageError);
      }

      // Agregar la URL del avatar al perfil
      if (imageData?.url) {
        data.avatarUrl = imageData.url;
      }

      return data;
    } catch (error) {
      // Silenciar errores de locks de Supabase Auth (son temporales por competencia)
      if (error.message?.includes('Lock') || error.message?.includes('lock')) {
        console.warn('[useCurrentUserProfile] Error de lock temporal, reintentando...');
        // No lanzar el error para permitir que TanStack Query reintente
        throw error;
      }
      console.error('[useCurrentUserProfile] Error obteniendo perfil:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Query para obtener datos del perfil con TanStack Query
   */
  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile
  } = useQuery({
    queryKey: ['currentUserProfile', userId],
    queryFn: fetchUserProfile,
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error) => {
      console.error('[useCurrentUserProfile] Error en query:', error);
    },
    onSuccess: (data) => {
      console.log('[useCurrentUserProfile] Query exitoso:', data);
    }
  });

  /**
   * Función para refrescar manualmente los datos del perfil
   */
  const refreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      console.error('[useCurrentUserProfile] Error refrescando perfil:', error);
    }
  }, [refetchProfile]);

  /**
   * Función para obtener la URL del avatar principal
   * @returns {string} - URL del avatar o imagen por defecto
   */
  const getAvatarUrl = useCallback(() => {
    // Usar la URL del avatar que ya está incluida en userProfile
    // Si no hay avatar, retornar null para que CachedAvatarImage use el placeholder
    return userProfile?.avatarUrl || null;
  }, [userProfile?.avatarUrl]);

  /**
   * Función para obtener todas las URLs de las imágenes del usuario
   * @returns {string[]} - Array de URLs de imágenes
   */
  const getAllImageUrls = useCallback(() => {
    // Se implementará en Fase 6 con el sistema de caché
    return [];
  }, []);

  /**
   * Función para verificar si el usuario tiene avatar
   * @returns {boolean} - True si tiene avatar personalizado
   */
  const hasCustomAvatar = useCallback(() => {
    // Se implementará en Fase 6
    return false;
  }, []);

  /**
   * Efecto para suscribirse a cambios en tiempo real del perfil
   */
  useEffect(() => {
    if (!userId) return;

    console.log('[useCurrentUserProfile] Configurando suscripciones en tiempo real...');

    const componentId = componentIdRef.current;

    // Si no existe canal global para este usuario, crearlo
    if (!globalProfileChannels.has(userId)) {
      console.log(`[useCurrentUserProfile] Creando canal global para usuario: ${userId}`);
      
      const channel = supabase
        .channel(`user-profile-${userId}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`
        }, (payload) => {
          console.log('[useCurrentUserProfile] Cambio detectado en perfil:', payload);
          // Invalidar y refrescar el query del perfil
          queryClient.invalidateQueries(['currentUserProfile', userId]);
        })
        .on('postgres_changes', {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'user_images',
          filter: `user_ref_id=eq.${userId}`
        }, (payload) => {
          console.log('[useCurrentUserProfile] Cambio detectado en imágenes:', payload);
          // Invalidar y refrescar el query del perfil
          queryClient.invalidateQueries(['currentUserProfile', userId]);
        })
        .subscribe((status) => {
          console.log(`[useCurrentUserProfile] Status suscripción ${userId}:`, status);
        });

      // Guardar referencia al canal y contador de componentes
      globalProfileChannels.set(userId, {
        channel,
        componentCount: 1,
        componentIds: new Set([componentId])
      });
    } else {
      // Incrementar contador de componentes usando este canal
      const channelData = globalProfileChannels.get(userId);
      channelData.componentCount++;
      channelData.componentIds.add(componentId);
      console.log(`[useCurrentUserProfile] Canal existente para ${userId}. Componentes activos: ${channelData.componentCount}`);
    }

    // Función de limpieza
    return () => {
      console.log(`[useCurrentUserProfile] Desmontando componente ${componentId}`);
      
      const channelData = globalProfileChannels.get(userId);
      if (channelData) {
        channelData.componentCount--;
        channelData.componentIds.delete(componentId);
        
        console.log(`[useCurrentUserProfile] Componentes restantes para ${userId}: ${channelData.componentCount}`);
        
        // Si no hay más componentes usando este canal, limpiarlo
        if (channelData.componentCount <= 0) {
          console.log(`[useCurrentUserProfile] Limpiando canal para usuario: ${userId}`);
          supabase.removeChannel(channelData.channel);
          globalProfileChannels.delete(userId);
        }
      }
    };
  }, [userId, queryClient]);

  /**
   * Estado de loading combinado
   */
  const isLoading = isLoadingProfile;

  /**
   * Error combinado
   */
  const error = profileError;

  /**
   * Objeto con funciones de utilidad para el avatar
   */
  const avatarUtils = {
    getAvatarUrl,
    getAllImageUrls,
    hasCustomAvatar,
    imageCount: 0 // Se implementará en Fase 6
  };

  /**
   * Datos básicos del usuario para fácil acceso
   */
  const basicInfo = userProfile ? {
    id: userProfile.id,
    fullName: userProfile.full_name || 'Usuario',
    bio: userProfile.bio || '',
    age: userProfile.years || 0,
    sex: userProfile.sex || 0,
    sexPreference: userProfile.sex_pref || 0,
    relationshipStatus: userProfile.situacionamorosa || 0,
    height: userProfile.altura || 0,
    weight: userProfile.peso || 0,
    location: userProfile.place_status || 0,
    lastConnection: userProfile.ultimaconexion || null
  } : null;

  return {
    // Datos completos del perfil
    userProfile,
    basicInfo,

    // Estados
    isLoading,
    error,

    // Funciones de utilidad para avatar
    avatar: avatarUtils,

    // Funciones de control
    refreshProfile,

    // Sesión para referencia
    session,
    userId
  };
};
